
import { Component, Vue, Watch } from "vue-property-decorator";
import * as Model from "@gigalot/data-models";
import lodash from "lodash";
import BreadcrumbTrail from "@/components/BreadcrumbTrail.vue";
import { homeBreadcrumbs } from "@/utils/breadcrumbs";

type JobDeliveryStatus = "notAllocated" | "allocated" | "delivered" | "partial" | "over" | "";

interface FeedAllocationLike {
  feedAllocated?: number;
  actualFed?: number;
  allocated?: boolean;
}

interface LineFeedingRow {
  active: boolean;
  actionDate?: number;
  description: string;
  dryMatterPercent?: number;
  gender: string;
  kgPerAnimalDry?: number;
  kgPerAnimalWet?: number;
  kraalAverageMass?: number;
  kraalGuid: string;
  kraalId: string;
  numberAnimals: number;
  owner: string;
  patchAmount: number;
  patchStatus: JobDeliveryStatus;
  ration: string;
  rationColour?: string;
  sessionAllocations: number[];
  sessionPercentages: Array<number | undefined>;
  sessionStatuses: JobDeliveryStatus[];
  totalAllocatedDry: number;
  totalAllocatedWet: number;
}

@Component({
  components: {
    BreadcrumbTrail,
  },
})
export default class LineFeedingOverview extends Vue {
  loading = false;
  errorText = "";
  search = "";
  selectedLineGuid = "";
  lines: Model.Line[] = [];
  feedPlanItems: Model.FeedingPlanItem[] = [];
  feedScheduleItems: Model.FeedingScheduleItem[] = [];
  rations: Model.Ration[] = [];
  owners: Model.CustomFeeder[] = [];

  readonly sessionColumnCount = 3;
  readonly actualColumnCount = 10;

  mounted() {
    this.$store.dispatch("user/addFirebaseCallback", this.loadData);
  }

  get themeClass(): string {
    return this.$store.state.lightDarkMode === "dark" ? "line-feeding-table-wrap--dark" : "line-feeding-table-wrap--light";
  }

  get breadcrumbs() {
    return homeBreadcrumbs({ text: "Line Feeding View", disabled: true });
  }

  get moment() {
    return this.$store.state.moment;
  }

  get allocationColumnCount(): number {
    return 5 + this.sessionColumnCount;
  }

  get lineOptions(): Model.Line[] {
    return [...this.lines].sort((left, right) => {
      if (left.active !== right.active) {
        return left.active ? -1 : 1;
      }

      return this.formatLineLabel(left).localeCompare(this.formatLineLabel(right), undefined, { numeric: true, sensitivity: "base" });
    });
  }

  get selectedLine(): Model.Line | undefined {
    return this.lines.find((line) => line.guid === this.selectedLineGuid);
  }

  get filteredRows(): LineFeedingRow[] {
    const rows = this.lineRows;
    const search = this.search.trim().toLowerCase();
    if (!search) return rows;

    return rows.filter((row) => {
      const haystack = [row.kraalId, row.owner, row.description, row.ration, row.gender].join(" ").toLowerCase();
      return haystack.includes(search);
    });
  }

  get totalAnimals(): number {
    return this.filteredRows.reduce((total, row) => total + (Number(row.numberAnimals) || 0), 0);
  }

  get totalAllocatedWet(): number {
    return this.filteredRows.reduce((total, row) => total + (Number(row.totalAllocatedWet) || 0), 0);
  }

  get statusLegend() {
    return [
      { key: "notAllocated" as JobDeliveryStatus, label: "Not allocated" },
      { key: "allocated" as JobDeliveryStatus, label: "Job allocated" },
      { key: "delivered" as JobDeliveryStatus, label: "Correct delivery" },
      { key: "partial" as JobDeliveryStatus, label: "Partial delivery" },
      { key: "over" as JobDeliveryStatus, label: "Over delivery" },
    ];
  }

  get sessionHeaders(): string[] {
    const rows = this.lineRows;

    return Array.from({ length: this.sessionColumnCount }, (_, index) => {
      const percentages = rows
        .map((row) => row.sessionPercentages[index])
        .filter((value): value is number => typeof value === "number" && !Number.isNaN(value));

      if (!percentages.length) {
        return `S${index + 1}`;
      }

      const counts = lodash.countBy(percentages, (value) => this.formatPercentLabel(value));
      const mostCommon = Object.keys(counts).sort((left, right) => counts[right] - counts[left])[0];
      return mostCommon ? `S${index + 1} (${mostCommon})` : `S${index + 1}`;
    });
  }

  get lineRows(): LineFeedingRow[] {
    const selectedLine = this.selectedLine;
    if (!selectedLine || !Array.isArray(selectedLine.kraals)) return [];

    const kraals = [...selectedLine.kraals].sort((left, right) =>
      (left.kraalId || "").localeCompare(right.kraalId || "", undefined, { numeric: true, sensitivity: "base" })
    );

    return kraals.map((kraal) => {
      const planItem = this.findPlanItem(kraal);
      const scheduleItem = this.findScheduleItem(kraal);
      const rationGuid = planItem?.currentRation?.rationGuid || scheduleItem?.rationGuid;
      const rationName = planItem?.currentRation?.ration || scheduleItem?.ration || "";
      const dryMatterFactor = this.getDryMatterFactor(rationGuid, rationName);
      const numberAnimals = Number(planItem?.numberAnimals) || 0;
      const totalAllocatedWet = Number(scheduleItem?.feedAllocated) || 0;
      const totalAllocatedDry = totalAllocatedWet * dryMatterFactor;
      const feedAllocations = Array.isArray(scheduleItem?.feedAllocations) ? scheduleItem!.feedAllocations : [];
      const sessionAllocations = Array.from({ length: this.sessionColumnCount }, (_, index) => Number(feedAllocations[index]?.feedAllocated) || 0);
      const sessionStatuses = Array.from({ length: this.sessionColumnCount }, (_, index) => this.getAllocationStatus(feedAllocations[index]));
      const sessionPercentages = Array.from({ length: this.sessionColumnCount }, (_, index) => {
        const value = feedAllocations[index]?.percentage;
        return typeof value === "number" ? value : undefined;
      });
      const patchAllocations = feedAllocations.slice(this.sessionColumnCount);
      const patchAmount = patchAllocations.reduce((total, allocation) => total + (Number(allocation?.feedAllocated) || 0), 0);
      const patchStatus = patchAllocations.length ? this.getAllocationStatus(this.mergeAllocations(patchAllocations)) : "";

      return {
        active: kraal.active,
        actionDate: planItem?.currentRation?.startDate || planItem?.time,
        description: planItem?.lotDescription || kraal.description || "",
        dryMatterPercent: rationName ? dryMatterFactor * 100 : undefined,
        gender: planItem?.gender || "",
        kgPerAnimalDry: numberAnimals > 0 ? totalAllocatedDry / numberAnimals : undefined,
        kgPerAnimalWet: numberAnimals > 0 ? totalAllocatedWet / numberAnimals : undefined,
        kraalAverageMass: planItem?.kraalAverageMass,
        kraalGuid: kraal.guid,
        kraalId: kraal.kraalId,
        numberAnimals,
        owner: this.lookupOwner(planItem?.owner),
        patchAmount,
        patchStatus,
        ration: rationName,
        rationColour: planItem?.currentRation?.colour,
        sessionAllocations,
        sessionPercentages,
        sessionStatuses,
        totalAllocatedDry,
        totalAllocatedWet,
      };
    });
  }

  @Watch("selectedLineGuid")
  onSelectedLineGuidChanged(nextValue: string) {
    const currentValue = typeof this.$route.query.line === "string" ? this.$route.query.line : "";
    if (nextValue === currentValue) return;

    const nextQuery = { ...this.$route.query };
    if (nextValue) {
      nextQuery.line = nextValue;
    } else {
      delete nextQuery.line;
    }

    this.$router.replace({ path: this.$route.path, query: nextQuery }).catch(() => undefined);
  }

  formatLineLabel(line: Model.Line): string {
    if (line.displayName && line.code) {
      return `${line.displayName} (${line.code})`;
    }
    return line.displayName || line.code || "Unnamed line";
  }

  formatDate(value?: number): string {
    if (!value) return "";
    return this.moment(value).format("YYYY/MM/DD");
  }

  formatDecimal(value?: number, decimals: number = 2, blankWhenZero: boolean = false): string {
    if (value === undefined || value === null || Number.isNaN(value)) return "";
    if (blankWhenZero && Number(value) === 0) return "";
    return Number(value).toFixed(decimals);
  }

  formatPercentLabel(value: number): string {
    return Number.isInteger(value) ? `${value}%` : `${(value*100).toFixed(1)}%`;
  }

  statusClass(status: JobDeliveryStatus): string {
    return status ? `line-feeding-job-cell--${status}` : "";
  }

  rationPillStyle(colour?: string) {
    if (!colour) {
      return {};
    }

    return {
      backgroundColor: colour.toLowerCase(),
      color: "#111827",
    };
  }

  genderClass(gender: string): string {
    switch ((gender || "").toLowerCase()) {
      case "male":
        return "line-feeding-pill--male";
      case "female":
        return "line-feeding-pill--female";
      default:
        return "line-feeding-pill--mix";
    }
  }

  lookupOwner(owner?: string): string {
    if (!owner) return "";
    const ownerLookup = this.owners.find((item) => item.guid === owner);
    return ownerLookup?.name || owner;
  }

  findPlanItem(kraal: Model.Kraal): Model.FeedingPlanItem | undefined {
    return this.feedPlanItems.find((item) => item.kraalGuid === kraal.guid || item.kraalId === kraal.kraalId);
  }

  findScheduleItem(kraal: Model.Kraal): Model.FeedingScheduleItem | undefined {
    return this.feedScheduleItems.find((item) => item.kraalGuid === kraal.guid || item.kraalId === kraal.kraalId);
  }

  mergeAllocations(allocations: FeedAllocationLike[]): FeedAllocationLike {
    return allocations.reduce<FeedAllocationLike>(
      (summary, allocation) => {
        summary.feedAllocated = (summary.feedAllocated || 0) + (Number(allocation?.feedAllocated) || 0);
        summary.actualFed = (summary.actualFed || 0) + (Number(allocation?.actualFed) || 0);
        summary.allocated = Boolean(summary.allocated || allocation?.allocated);
        return summary;
      },
      { feedAllocated: 0, actualFed: 0, allocated: false }
    );
  }

  getAllocationStatus(allocation?: FeedAllocationLike): JobDeliveryStatus {
    if (!allocation) return "";

    const feedAllocated = Number(allocation.feedAllocated) || 0;
    const actualFed = Number(allocation.actualFed) || 0;
    const tolerance = 0.5;

    if (feedAllocated <= tolerance && actualFed <= tolerance) {
      return "notAllocated";
    }

    if (actualFed > feedAllocated + tolerance) {
      return "over";
    }

    if (feedAllocated > tolerance && actualFed >= feedAllocated - tolerance) {
      return "delivered";
    }

    if (actualFed > tolerance) {
      return "partial";
    }

    if (allocation.allocated) {
      return "allocated";
    }

    return "notAllocated";
  }

  getDryMatterFactor(rationGuid?: string, rationName?: string): number {
    let ration: Model.Ration | undefined;

    if (rationGuid) {
      ration = this.rations.find((item) => item.guid === rationGuid);
    }

    if (!ration && rationName) {
      ration = this.rations.find((item) => item.name === rationName);
    }

    return ration?.dryMatterFactor?.dryMatterFactor || 1;
  }

  initializeSelectedLine() {
    const routeLineGuid = typeof this.$route.query.line === "string" ? this.$route.query.line : "";
    const hasRouteMatch = routeLineGuid && this.lineOptions.some((line) => line.guid === routeLineGuid);

    if (hasRouteMatch) {
      this.selectedLineGuid = routeLineGuid;
      return;
    }

    if (this.selectedLineGuid && this.lineOptions.some((line) => line.guid === this.selectedLineGuid)) {
      return;
    }

    this.selectedLineGuid = this.lineOptions[0]?.guid || "";
  }

  async loadData() {
    this.loading = true;
    this.errorText = "";

    try {
      const gql = `query LineFeedingOverview($guid: String!) {
        Lines(guid: $guid) {
          guid
          code
          displayName
          active
          kraals {
            guid
            kraalId
            description
            active
            lineGuid
          }
        }
        FeedPlan(guid: $guid) {
          FeedingPlanItems {
            guid
            kraalId
            kraalGuid
            numberAnimals
            owner
            gender
            kraalAverageMass
            lotDescription
            time
            currentRation {
              ration
              rationGuid
              startDate
              colour
            }
          }
        }
        FeedSchedule(guid: $guid) {
          feedingScheduleItems {
            guid
            kraalId
            kraalGuid
            ration
            rationGuid
            feedAllocated
            feedAllocations {
              feedAllocated
              actualFed
              allocated
              percentage
            }
          }
        }
        Rations(guid: $guid) {
          guid
          name
          dryMatterFactor {
            dryMatterFactor
          }
        }
        customFeeders(guid: $guid) {
          guid
          name
          active
        }
      }`;

      const response = await this.$store.dispatch("graphQl", {
        gql,
        variables: { guid: this.$store.state.user.location.guid },
      });

      this.lines = (response?.data?.Lines ?? []) as Model.Line[];
      this.feedPlanItems = (response?.data?.FeedPlan?.FeedingPlanItems ?? []) as Model.FeedingPlanItem[];
      this.feedScheduleItems = (response?.data?.FeedSchedule?.feedingScheduleItems ?? []) as Model.FeedingScheduleItem[];
      this.rations = (response?.data?.Rations ?? []) as Model.Ration[];
      this.owners = (response?.data?.customFeeders ?? []) as Model.CustomFeeder[];

      this.initializeSelectedLine();
    } catch (err) {
      console.log("loadData -> error:", err);
      this.errorText = `Error downloading line feeding data: ${err}`;
    } finally {
      this.loading = false;
    }
  }
}
